import toast from "react-hot-toast";
import { getUnavailableVehicleDataV2 } from "api/vehicles/vehicle";
import {
  createDateWithoutTimezone,
  formatDate,
  parsePartialUnavailability,
} from "@1-now/booking-core";

/**
 * The date pickers' availability read, written once (1NOW-1659).
 *
 * Five pickers each carried their own copy: the hero's date range, From,
 * Until, the date-range input and the edit-booking calendar. The copies were
 * word for word the same apart from the edit flow's options, so a fix to one
 * had to be made five times, on the booking path.
 *
 * A picker with no car does nothing and says nothing. The home page's pickers
 * have no car by design, so every copy used to log "No car selected, unable
 * to fetch unavailable dates." on every home-page visit. A warning about the
 * expected case only teaches people to ignore the console.
 */

export type PartialUnavailability = {
  [date: string]: { from: string; to: string }[];
};

export interface UnavailabilityQuery {
  fleetId?: number | null;
  locationId?: number | null;
  /** The booking being changed, so its own days do not read as taken. */
  bookingToExclude?: number | string | null;
  /** Months fetched past the visible range. The edit flow reads 3. */
  monthsAhead?: number;
  /** Leave advance-notice blocks out of the answer. The edit flow sets this. */
  excludeAdvanceNotice?: boolean;
}

/** Where the answer goes: the picker's own state, and its parent's copy when it was given one. */
export interface UnavailabilitySinks {
  setLoading: (loading: boolean) => void;
  setFullyUnavailable: (dates: Date[]) => void;
  setPartialUnavailability: (partial: PartialUnavailability) => void;
  setPartiallyUnavailableTimes?: (partial: any) => void;
}

export const loadUnavailability = async (
  startDate: Date,
  endDate: Date,
  query: UnavailabilityQuery,
  sinks: UnavailabilitySinks,
): Promise<void> => {
  const { fleetId } = query;
  if (!fleetId) return;

  sinks.setLoading(true);
  try {
    // A month either side of what is on screen, so paging the calendar a
    // month does not open onto days nobody asked about.
    const from = new Date(startDate);
    from.setMonth(from.getMonth() - 1);
    const to = new Date(endDate);
    to.setMonth(to.getMonth() + (query.monthsAhead ?? 1));

    const response = await getUnavailableVehicleDataV2(
      {
        start_date: formatDate(from),
        end_date: formatDate(to),
        booking_to_exclude: query.bookingToExclude,
      },
      fleetId,
      query.locationId ?? undefined,
      query.excludeAdvanceNotice,
    );

    // A failed request comes back as { error } rather than throwing, so the
    // calendar keeps what it had, as every copy did before.
    if (response.data) {
      const data = response.data as {
        full_unavailable: string[];
        partial_unavailable: { [date: string]: [string, string][] };
      };
      sinks.setFullyUnavailable(
        data.full_unavailable.map((dateStr) => createDateWithoutTimezone(dateStr)),
      );
      const partial = parsePartialUnavailability(data.partial_unavailable);
      sinks.setPartialUnavailability(partial);
      sinks.setPartiallyUnavailableTimes?.(partial);
    }
  } catch (error) {
    console.error("Error fetching unavailable dates:", error);
    toast.error("Couldn't load availability. Please try again.");
  } finally {
    sinks.setLoading(false);
  }
};
